<script setup lang="ts">
import { ref, storeToRefs, useForm, watch } from '#imports';
import { toTypedSchema } from '@vee-validate/valibot';
import { object, optional, pipe, string, nonEmpty, email, minLength, boolean } from 'valibot';
import { errorValidationMessages, responseMessages } from '@/app/messages';

import { useUserAuthStore } from '@/entities/User/store/auth';
import { useUserDialogStore } from './../store/dialog';
import { useDialogResponseStore } from '@/widgets/dialog-response/store';
import { Validation } from '@/app/rules';
import { CaptchaProcessEnum, useCaptchaStore } from '@/features/captcha/store';

const { setResponseModalMessage, showResponseModal } = useDialogResponseStore();
const { showSpecialUserDialog, closeUserDialog } = useUserDialogStore();

const { defineField, handleSubmit, handleReset, errors } = useForm({
	validationSchema: toTypedSchema(
		object({
			username: optional(pipe(
				string(),
				email(errorValidationMessages.required.email),
				nonEmpty(errorValidationMessages.required.email),
			), ''),
			password: optional(pipe(
				string(),
				minLength(Validation.Password.minLength, errorValidationMessages.regex.password),
			), ''),
			remember: optional(boolean()),
		}),
	),
	initialValues: {
		username: '',
		password: '',
		remember: false,
	},
});

const [usernameModel, usernameAttrs] = defineField('username'); // email-login
const [passwordModel, passwordAttrs] = defineField('password');
const [rememberModel, rememberAttrs]: any = defineField('remember');

const errorResponse = ref<null | Record<string, Array<string>>>(null);
const isLoading = ref(false);

const { login, profile } = useUserAuthStore();

/** Handle Submit */
const captchaStore = useCaptchaStore();
const { captchaToken, captchaProcess } = storeToRefs(captchaStore);
const { captchaExecute, captchaReset } = captchaStore;

const payload = ref();

const onSubmit = handleSubmit((model) => {
	payload.value = model;
	captchaExecute(CaptchaProcessEnum.Login);
});

watch(captchaToken, async (val) => {
	if (val.length  && captchaProcess.value === CaptchaProcessEnum.Login) {
		errorResponse.value = null;
		isLoading.value = true;

		const response = await login({
			...payload.value,
			captcha_token: val,
		});

		captchaReset();
		isLoading.value = false;

		if ('error' in response) {
			errorResponse.value = response.error as any;
			return;
		} else {
			profile();
		}

		handleReset();
		setResponseModalMessage(responseMessages.success.login);
		setTimeout(() => showResponseModal(), 0);
		closeUserDialog();
	}
});

/** Checkbox */
const isDescriptionOpen = ref(false);
</script>

<template>
	<form class="form" @submit.prevent="onSubmit">
		<div class="form__block">
			<div class="form__field">
				<v-input
					v-model="usernameModel"
					v-bind="usernameAttrs"
					type="email"
					placeholder="Логин"
					name="email"
					required
					:is-error="!!errors.username"
					:error-message="errors.username"
				/>
			</div>

			<div class="form__field">
				<v-input
					v-model="passwordModel"
					v-bind="passwordAttrs"
					type="password"
					placeholder="Пароль"
					name="password"
					required
					:is-error="!!errors.password"
					:error-message="errors.password"
				/>
			</div>

			<div class="form__field">
				<v-checkbox
					v-model="rememberModel"
					v-bind="rememberAttrs"
					:value="true"
				>
					Запомнить меня.
					<v-link
						href="#"
						color="black"
						:variations="['underline']"
						@click="isDescriptionOpen = !isDescriptionOpen"
					>
						Что это значит?
					</v-link>
				</v-checkbox>
				<transition name="disclosure">
					<p v-show="isDescriptionOpen" class="text-mini mt-8">
						Поставьте галочку в этом поле, если не хотите авторизовываться каждый раз при входе на сайт. Вы сможете выйти из своего аккаунта в любой момент. Мы не рекомендуем выбирать эту опцию, если доступ к устройству, которое вы используете, есть у кого-то еще.
					</p>
				</transition>
			</div>

			<div class="form__field">
				<v-link
					href="#"
					:variations="['underline']"
					class="text-mini"
					@click.prevent="showSpecialUserDialog('password')"
				>
					Забыли пароль?
				</v-link>
			</div>
		</div>

		<div class="form__block">
			<v-button
				type="submit"
				:disabled="isLoading"
				class="form__submit"
			>
				Войти
			</v-button>
		</div>

		<div
			v-if="errorResponse !== null"
			class="form__block form__block--error"
		>
			<p
				v-for="(error, key) in errorResponse"
				:key="key"
				class="text-small mb-8"
			>
				{{ error }}
			</p>
		</div>
	</form>
</template>
